import { useRef, useState, useEffect, Dispatch, SetStateAction, useCallback } from 'react'
import { useDebounce } from 'shared-components/hooks/use-debaunce'

interface UseSearchOpts<T> {
  pending: boolean
  results: T | null
  setValue: Dispatch<SetStateAction<string>>
  value: string
  clearResults: () => void
  currentQuery: string
}

export type SearchHandler<T> = (query: string) => Promise<T | null>

export function useSearch<T>(searchHandler: SearchHandler<T>): UseSearchOpts<T> {
  const search = useRef<SearchHandler<T>>()
  const [pending, setPending] = useState(false)
  const [results, setResults] = useState<T | null>(null)
  const [value, setValue] = useState('')

  useEffect(() => {
    search.current = searchHandler
  }, [searchHandler])

  const currentQuery = useDebounce(value, 300)
  useEffect(() => {
    if (currentQuery) {
      void (async () => {
        const { current } = search
        if (current) {
          setPending(true)
          setResults(await current(currentQuery))
          setPending(false)
        }
      })()
    }
  }, [currentQuery])

  return {
    currentQuery,
    pending,
    results,
    setValue,
    value,
    clearResults: useCallback(() => {
      setResults(null)
      setValue('')
    }, []),
  }
}
